import MessageApi from '@/misc/apis/MessageApi'

export type EmailPdfRecipient = {
  name?: string
  email: string
}

export type EmailPdfLabels = {
  from: string
  to: string
  cc: string
  bcc: string
  subject: string
}

export type BuildPrintableOutboundEmailHtmlParams = {
  from: EmailPdfRecipient
  to: EmailPdfRecipient[]
  cc?: EmailPdfRecipient[]
  bcc?: EmailPdfRecipient[]
  subject: string
  bodyHtml: string
  labels: EmailPdfLabels
  includeHeader?: boolean
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function formatMailRecipient(recipient: EmailPdfRecipient): string {
  const email = escapeHtml(recipient.email || '')
  if (recipient.name) {
    return `${escapeHtml(recipient.name)} &lt;${email}&gt;`
  }
  return email
}

function formatRecipientList(recipients: EmailPdfRecipient[]): string {
  return recipients.map(formatMailRecipient).join(', ')
}

function buildHeaderRow(label: string, value: string): string {
  if (!value) {
    return ''
  }
  return `
    <div class="col-12 mb-1 row">
      <label class="col-2">${escapeHtml(label)}</label>
      <span class="col-10">${value}</span>
    </div>`
}

export function buildPrintableOutboundEmailHtml({
  from,
  to,
  cc = [],
  bcc = [],
  subject,
  bodyHtml,
  labels,
  includeHeader = true
}: BuildPrintableOutboundEmailHtmlParams): string {
  const toValue = includeHeader ? formatRecipientList(to) : ''
  const ccValue = includeHeader ? formatRecipientList(cc) : ''
  const bccValue = includeHeader ? formatRecipientList(bcc) : ''

  return `
    <div id="bodyMessage" class="row">
      <div class="col">
        ${
          includeHeader
            ? `
              <div class="row">
                ${buildHeaderRow(labels.from, formatMailRecipient(from))}
                ${buildHeaderRow(labels.to, toValue)}
                ${buildHeaderRow(labels.cc, ccValue)}
                ${buildHeaderRow(labels.bcc, bccValue)}
                <div class="col-12 row">
                  <label class="col-2">${escapeHtml(labels.subject)}</label>
                  <span class="col-10">${escapeHtml(subject || '')}</span>
                  <hr>
                </div>
              </div>
            `
            : ''
        }
        <div class="row">
          <div class="col-12 printMessageBodyEmailContent">
            ${bodyHtml}
          </div>
        </div>
      </div>
    </div>`
}

export async function renderEmailPdfBlob(printableHtml: string): Promise<Blob> {
  const cssResponse = await fetch('/printEmailScss.css')
  const cssText = await cssResponse.text()
  const contentHTML = `
    <html>
      <head>
        <style>${cssText}</style>
      </head>
      <body>
        ${printableHtml}
      </body>
    </html>`

  const formData = new FormData()
  const htmlBlob = new Blob([contentHTML], { type: 'text/html' })
  formData.append('files', htmlBlob, 'index.html')

  const response = await MessageApi.renderPDF(formData)
  return new Blob([response], { type: 'application/pdf' })
}
